import dayjs, { Dayjs } from "dayjs";
import { SpecificDayValue } from "./types";
import { DATE_FORMATS } from "./constants";

export const checkIsSpecificDayValue = (
  value: Record<string, any>,
): value is SpecificDayValue => {
  return ["day", "month", "year"].every(
    (item: string) =>
      item in value && (typeof value[item] === "number" || value[item] == null),
  );
};

export const checkIsSpecificDayValueFilled = (
  specificDayValue: SpecificDayValue,
) => {
  return Object.entries(specificDayValue)
    .filter(([key]) => ["day", "month", "year"].includes(key))
    .some(([_, value]) => value !== null);
};

export const getInitialSpecificDayValue = (): SpecificDayValue => ({
  day: null,
  month: null,
  year: null,
});

export const formatSpecificDayValue = (
  specificDayValue: SpecificDayValue,
  format?: string,
  isDaysOfWeekEnabled = true,
): { date: Dayjs; formattedValue: string } => {
  const date = dayjs({
    day: specificDayValue.day ?? undefined,
    month: specificDayValue.month ?? undefined,
    year: specificDayValue.year ?? undefined,
  });

  const day = date.format(DATE_FORMATS.DAY_NUMBER);
  const month = date.format(DATE_FORMATS.MONTH_NUMBER);
  const year = date.format(DATE_FORMATS.YEAR_FULL);

  const localizedFormatString =
    dayjs.Ls[date.locale()].formats[format as keyof ILocale["formats"]] ||
    format;

  if (!localizedFormatString) {
    return {
      date,
      formattedValue: date.format(format),
    };
  }

  let formattedValue = localizedFormatString;

  if (specificDayValue.day !== null) {
    formattedValue = formattedValue.replace(
      DATE_FORMATS.DAY_NUMBER,
      specificDayValue.isWeekday && isDaysOfWeekEnabled
        ? dayjs()
            .weekday(specificDayValue.day)
            .format(DATE_FORMATS.DAY_NAME_OF_WEEK_SHORT)
            .toLocaleUpperCase()
        : day,
    );
  }

  if (specificDayValue.month !== null) {
    formattedValue = formattedValue.replace(DATE_FORMATS.MONTH_NUMBER, month);
  }

  if (specificDayValue.year !== null) {
    formattedValue = formattedValue.replace(DATE_FORMATS.YEAR_FULL, year);
  }

  return {
    date,
    formattedValue,
  };
};

export const spotPickerValues = <T>(
  value?: T | SpecificDayValue,
): {
  pickerValue: T | null;
  specificDayValue: SpecificDayValue | null;
} => {
  if (!value) {
    return {
      pickerValue: null,
      specificDayValue: null,
    };
  }

  if (checkIsSpecificDayValue(value)) {
    return {
      pickerValue: null,
      specificDayValue: value,
    };
  }

  return {
    pickerValue: value,
    specificDayValue: null,
  };
};
